import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, Calculator, Layers, ArrowRight, ChevronRight, Check, Sparkles, TrendingUp, ArrowLeftRight } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const steps = [
  {
    id: 1,
    path: '/strength-scanner',
    icon: Shield,
    color: '#E85A1C',
    label: 'Step 1',
    title: 'Strength Scanner',
    description: 'Compare Lion Cement against market alternatives across 5 key parameters. Find out which cement is best for your project.',
    action: 'Start Comparing',
    features: ['Visual comparison grid', '5 strength parameters', 'Location-based analysis', 'Dynamic verdict'],
  },
  {
    id: 2,
    path: '/savings-calculator',
    icon: Calculator,
    color: '#10b981',
    label: 'Step 2',
    title: 'Savings Calculator',
    description: 'See exactly how much you save by choosing Lion Cement. Get a shareable savings card for WhatsApp.',
    action: 'Calculate Savings',
    features: ['Real-time savings', 'Price comparison', 'WhatsApp share card', 'Find nearest dealer'],
  },
  {
    id: 3,
    path: '/calculator',
    icon: Layers,
    color: '#3b82f6',
    label: 'Step 3',
    title: 'Project Calculator',
    description: 'Estimate all materials needed — cement bags, sand, blocks, adhesive, plaster — for your specific project.',
    action: 'Estimate Materials',
    features: ['5 project types', '3 unit systems (m/ft/in)', 'Material quantities', 'Get exact quote'],
  },
];

const ToolsHubPage = () => {
  const [activeStep, setActiveStep] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { trackPageView('/tools', 'Smart Tools Hub'); }, []);

  return (
    <div className="min-h-screen bg-void" data-testid="tools-hub-page">
      <SEOHead
        title="Smart Tools Hub"
        description="Free interactive construction tools — Project Calculator, Strength Scanner, Savings Calculator, Product Comparison, Dealer Earnings, and Construction Quiz."
        path="/tools"
      />
      {/* Hero */}
      <section className="relative py-12 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        {/* Animated gradient orbs */}
        <motion.div className="absolute top-1/4 -left-32 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(232,90,28,0.12) 0%, transparent 60%)' }}
          animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 60%)' }}
          animate={{ scale: [1, 1.15, 1], y: [0, -30, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }} />
        <motion.div className="absolute top-1/2 right-1/4 w-[300px] h-[300px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 60%)' }}
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 4 }} />

        <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-3xl mx-auto">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-surface border border-white/10 rounded-full px-5 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-coral" />
              <span className="text-gray-300 font-display text-sm font-medium">Smart Construction Tools</span>
            </motion.div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Compare. Save.<br /><span className="text-gradient">Build Smart.</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Three powerful tools that work together — helping you choose the right cement, calculate your savings, and estimate your project materials. All in one place.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Connected Steps Flow */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          {/* Flow line */}
          <div className="relative">
            {/* Vertical connection line */}
            <div className="hidden md:block absolute left-[calc(50%-1px)] top-0 bottom-0 w-0.5 bg-gradient-to-b from-coral via-mint to-blue-500 opacity-20" />

            {steps.map((step, index) => (
              <StepCard key={step.id} step={step} index={index} activeStep={activeStep}
                onHover={setActiveStep} onGo={() => navigate(step.path)} />
            ))}
          </div>

          {/* Quick launch strip */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-16 bg-surface border border-white/5 rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6 justify-between">
              <div>
                <h3 className="font-display text-xl font-bold text-white mb-1">Ready to get started?</h3>
                <p className="text-gray-400 text-sm">Start from Step 1 for the full experience, or jump to any tool directly.</p>
              </div>
              <Link to="/strength-scanner" data-testid="tools-hub-start-btn"
                className="group flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-8 py-4 rounded-full transition-all whitespace-nowrap">
                Start from Step 1 <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>

          {/* Dealer Earnings Tool */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
            className="mt-8">
            <Link to="/dealer-earnings" data-testid="tools-hub-dealer-earnings"
              className="block bg-surface border border-avi-gold/20 hover:border-avi-gold/40 rounded-2xl p-6 md:p-8 transition-all group">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-14 h-14 rounded-2xl bg-avi-gold/10 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-7 h-7 text-avi-gold" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <span className="text-avi-gold font-mono text-xs uppercase tracking-wider">For Dealers</span>
                  <h3 className="font-display text-xl font-bold text-white mt-1">Dealer Earnings Calculator</h3>
                  <p className="text-gray-400 text-sm mt-1">See how much more you earn with Lion Cement vs competitive brands. No ceiling on earnings.</p>
                </div>
                <ArrowRight className="w-6 h-6 text-avi-gold group-hover:translate-x-2 transition-transform" />
              </div>
            </Link>
          </motion.div>

          {/* Product Comparison Tool */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
            className="mt-8">
            <Link to="/compare" data-testid="tools-hub-comparison"
              className="block bg-surface border border-[#3b82f6]/20 hover:border-[#3b82f6]/40 rounded-2xl p-6 md:p-8 transition-all group">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-14 h-14 rounded-2xl bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0">
                  <ArrowLeftRight className="w-7 h-7 text-[#3b82f6]" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <span className="text-[#3b82f6] font-mono text-xs uppercase tracking-wider">New Tool</span>
                  <h3 className="font-display text-xl font-bold text-white mt-1">Product Comparison Tool</h3>
                  <p className="text-gray-400 text-sm mt-1">Do products ko side-by-side compare karein — specs, grades, use cases sab ek jagah dekhein.</p>
                </div>
                <ArrowRight className="w-6 h-6 text-[#3b82f6] group-hover:translate-x-2 transition-transform" />
              </div>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const StepCard = ({ step, index, activeStep, onHover, onGo }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const isActive = activeStep === step.id;

  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className={`relative mb-8 ${index % 2 === 0 ? 'md:pr-[52%]' : 'md:pl-[52%]'}`}
      onMouseEnter={() => onHover(step.id)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Step number badge on line */}
      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-8 z-10">
        <motion.div
          animate={isActive ? { scale: 1.2 } : { scale: 1 }}
          className="w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-sm"
          style={{ backgroundColor: step.color, color: step.id === 2 ? '#0a0a0b' : 'white' }}>
          {step.id}
        </motion.div>
      </div>

      <motion.div
        animate={isActive ? { scale: 1.02, borderColor: `${step.color}40` } : { scale: 1, borderColor: 'rgba(255,255,255,0.05)' }}
        className="bg-surface border rounded-2xl p-6 md:p-8 transition-all cursor-pointer group"
        onClick={onGo}
        data-testid={`tools-step-${step.id}`}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${step.color}15` }}>
            <step.icon className="w-5 h-5" style={{ color: step.color }} />
          </div>
          <div>
            <span className="text-xs font-mono uppercase tracking-wider" style={{ color: step.color }}>{step.label}</span>
            <h3 className="font-display text-xl font-bold text-white">{step.title}</h3>
          </div>
        </div>

        <p className="text-gray-400 text-sm mb-5 leading-relaxed">{step.description}</p>

        <div className="grid grid-cols-2 gap-2 mb-6">
          {step.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
              <Check className="w-3 h-3 flex-shrink-0" style={{ color: step.color }} />
              <span>{f}</span>
            </div>
          ))}
        </div>

        <button className="flex items-center gap-2 font-display text-sm font-medium group-hover:gap-3 transition-all"
          style={{ color: step.color }}>
          {step.action} <ChevronRight className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Arrow connector */}
      {index < 2 && (
        <div className="flex justify-center my-2 md:hidden">
          <motion.div animate={{ y: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
            className="text-gray-600"><ChevronRight className="w-6 h-6 rotate-90" /></motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default ToolsHubPage;
